import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket debe usarse dentro de un SocketProvider');
  }
  return context;
};

const SOCKET_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3000';

export const SocketProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const messageListeners = useRef(new Map());
  const typingListeners = useRef(new Map());

  // Conectar al WebSocket cuando el usuario está autenticado
  useEffect(() => {
    if (!isAuthenticated) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const newSocket = io(`${SOCKET_URL}/chat`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('🔌 WebSocket conectado');
      setIsConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket desconectado:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('🔌 Error de conexión WebSocket:', error.message);
      setIsConnected(false);
    });

    // Escuchar usuarios online/offline
    newSocket.on('user:online', ({ userId }) => {
      setOnlineUsers((prev) => new Set([...prev, userId]));
    });

    newSocket.on('user:offline', ({ userId }) => {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    });

    // Escuchar nuevos mensajes
    newSocket.on('message:new', (data) => {
      const { conversacionId, mensaje } = data;
      const listeners = messageListeners.current.get(conversacionId) || [];
      listeners.forEach((listener) => listener(mensaje));
      
      // También notificar a los listeners globales (para actualizar lista de conversaciones)
      const globalListeners = messageListeners.current.get('global') || [];
      globalListeners.forEach((listener) => listener(data));
    });

    // Escuchar indicador de escritura
    newSocket.on('message:typing', (data) => {
      const { conversacionId, userId, isTyping } = data;
      const listeners = typingListeners.current.get(conversacionId) || [];
      listeners.forEach((listener) => listener({ userId, isTyping }));
    });

    // Escuchar mensajes leídos
    newSocket.on('message:read', (data) => {
      const listeners = messageListeners.current.get(`read:${data.conversacionId}`) || [];
      listeners.forEach((listener) => listener(data));
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [isAuthenticated]);

  // Enviar mensaje (con timeout y ack seguro; si no, el spinner del chat se queda colgado)
  const sendMessage = useCallback(async (conversacionId, content) => {
    if (!socket || !isConnected) {
      throw new Error('No conectado al servidor de chat');
    }

    const ACK_TIMEOUT_MS = 10000;

    return new Promise((resolve, reject) => {
      let settled = false;

      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        socket.off('disconnect', onDisconnect);
        fn(value);
      };

      const onDisconnect = () => {
        finish(reject, new Error('Conexión de chat perdida al enviar'));
      };

      const timer = setTimeout(() => {
        finish(reject, new Error('Tiempo de espera agotado al enviar el mensaje'));
      }, ACK_TIMEOUT_MS);

      socket.once('disconnect', onDisconnect);

      socket.emit('message:send', { conversacionId, content }, (response) => {
        if (!response) {
          finish(reject, new Error('Sin respuesta del servidor de chat'));
          return;
        }
        if (response.error) {
          finish(reject, new Error(response.error));
          return;
        }
        finish(resolve, response.mensaje ?? response);
      });
    });
  }, [socket, isConnected]);

  // Indicar que está escribiendo
  const sendTyping = useCallback((conversacionId, isTyping) => {
    if (socket && isConnected) {
      socket.emit('message:typing', { conversacionId, isTyping });
    }
  }, [socket, isConnected]);

  // Marcar como leído
  const markAsRead = useCallback((conversacionId) => {
    if (socket && isConnected) {
      socket.emit('message:read', { conversacionId });
    }
  }, [socket, isConnected]);

  // Unirse a una conversación (espera ack para no enviar antes de estar en la room)
  const joinConversation = useCallback((conversacionId) => {
    if (!socket || !isConnected || !conversacionId) {
      return Promise.resolve({ skipped: true });
    }

    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve({ timedOut: true }), 4000);
      socket.emit('conversation:join', { conversacionId }, (response) => {
        clearTimeout(timer);
        resolve(response || { success: true });
      });
    });
  }, [socket, isConnected]);

  // Suscribirse a mensajes de una conversación
  const subscribeToMessages = useCallback((conversacionId, callback) => {
    const listeners = messageListeners.current.get(conversacionId) || [];
    listeners.push(callback);
    messageListeners.current.set(conversacionId, listeners);

    // Devolver función para desuscribirse
    return () => {
      const updatedListeners = messageListeners.current.get(conversacionId) || [];
      const index = updatedListeners.indexOf(callback);
      if (index > -1) {
        updatedListeners.splice(index, 1);
        messageListeners.current.set(conversacionId, updatedListeners);
      }
    };
  }, []);

  // Suscribirse a indicador de escritura
  const subscribeToTyping = useCallback((conversacionId, callback) => {
    const listeners = typingListeners.current.get(conversacionId) || [];
    listeners.push(callback);
    typingListeners.current.set(conversacionId, listeners);

    return () => {
      const updatedListeners = typingListeners.current.get(conversacionId) || [];
      const index = updatedListeners.indexOf(callback);
      if (index > -1) {
        updatedListeners.splice(index, 1);
        typingListeners.current.set(conversacionId, updatedListeners);
      }
    };
  }, []);

  // Verificar si un usuario está online
  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  const value = {
    socket,
    isConnected,
    onlineUsers,
    sendMessage,
    sendTyping,
    markAsRead,
    joinConversation,
    subscribeToMessages,
    subscribeToTyping,
    isUserOnline,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;






